import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import {
  MarketApp,
  MarketApps,
  MarketAppWithTabs,
  TileBigNumber,
  WidgetV2
} from '@/features/Dashboard/types';
import { useMarketPreview } from '@/hooks/api/market';
import { SetState } from '@/types/helpers';

import {
  addWidgetWithTabs,
  appendTilesFromWidget,
  AppendTilesResult,
  buildPreviewConfig,
  updateTabInApps,
  updateTileInTiles
} from './utils';

type Handlers = {
  addWidget: (widget: MarketApp) => AppendTilesResult | void;
  updateTab: (appId: string, tabId: string, updatedTab: WidgetV2) => void;
  updateTile: (tileId: string, updatedTile: TileBigNumber) => void;
};

type EditDashboardDraftContextValue = {
  addWidget: (widget: MarketApp) => AppendTilesResult | void;
  updateTab: (appId: string, tabId: string, updatedTab: WidgetV2) => void;
  updateTile: (tileId: string, updatedTile: TileBigNumber) => void;
  marketTabApps: MarketAppWithTabs[];
  setMarketTabApps: SetState<MarketAppWithTabs[]>;
  tiles: TileBigNumber[];
  setTiles: SetState<TileBigNumber[]>;
  insertAtIndex: number | null;
  setInsertAtIndex: SetState<number | null>;
  insertAtId: string | null;
  setInsertAtId: SetState<string | null>;
  preview: any;
  isPreviewLoading: boolean;
  setIsDragging: SetState<boolean>;
  dashboardName: string;
  setDashboardName: SetState<string>;
  // New properties for nested structure support
  currentTabName: string;
  setCurrentTabName: SetState<string>;
  availableTabs: string[];
  setAvailableTabs: SetState<string[]>;
  initialDashboardName: string;
};

const EditDashboardDraftContext = createContext<EditDashboardDraftContextValue | undefined>(
  undefined
);

export const EditDashboardDraftProvider: React.FC<
  React.PropsWithChildren & { initialDashboardName: string }
> = ({ children, initialDashboardName }) => {
  const [marketTabApps, setMarketTabApps] = useState<MarketAppWithTabs[]>([]);
  const [tiles, setTiles] = useState<TileBigNumber[]>([]);
  const [insertAtIndex, setInsertAtIndex] = useState<number | null>(null);
  const [insertAtId, setInsertAtId] = useState<string | null>(null);
  const [dashboardName, setDashboardName] = useState<string>(initialDashboardName);

  useEffect(() => {
    setDashboardName(initialDashboardName);
  }, [initialDashboardName]);
  // New state for nested structure support
  const [currentTabName, setCurrentTabName] = useState<string>('Main');
  const [availableTabs, setAvailableTabs] = useState<string[]>(['Main']);

  const previewConfig: MarketApps = useMemo(() => {
    return buildPreviewConfig(tiles, marketTabApps);
  }, [tiles, marketTabApps]);
  const [isDragging, setIsDragging] = useState(false);

  const { data, isLoading } = useMarketPreview(previewConfig, !isDragging);

  const handlers: Handlers = useMemo(
    () => ({
      addWidget: (widget: MarketApp): AppendTilesResult | void => {
        if ('type' in widget && widget.type === 'tiles') {
          let result: AppendTilesResult | undefined;
          setTiles(prev => {
            result = appendTilesFromWidget(prev, widget);
            return result.tiles;
          });
          return result;
        }
        if ('tabs' in widget) {
          setMarketTabApps(prev => addWidgetWithTabs(prev, widget, insertAtId));
          // reset after use
          setInsertAtIndex(null);
          setInsertAtId(null);
        }
      },
      updateTab: (appId: string, tabId: string, updatedTab: WidgetV2) => {
        setMarketTabApps(prev => updateTabInApps(prev, appId, tabId, updatedTab));
      },
      updateTile: (tileId: string, updatedTile) => {
        setTiles(prev => updateTileInTiles(prev, tileId, updatedTile));
      }
    }),
    [insertAtId]
  );

  const value = useMemo<EditDashboardDraftContextValue>(
    () => ({
      addWidget: handlers.addWidget,
      updateTab: handlers.updateTab,
      updateTile: handlers.updateTile,
      marketTabApps,
      setMarketTabApps,
      tiles,
      setTiles,
      insertAtIndex,
      setInsertAtIndex,
      insertAtId,
      setInsertAtId,
      preview: data,
      isPreviewLoading: isLoading,
      setIsDragging,
      dashboardName,
      setDashboardName,
      // New properties for nested structure support
      currentTabName,
      setCurrentTabName,
      availableTabs,
      setAvailableTabs,
      initialDashboardName
    }),
    [
      handlers,
      marketTabApps,
      tiles,
      insertAtIndex,
      insertAtId,
      data,
      isLoading,
      dashboardName,
      currentTabName,
      availableTabs,
      initialDashboardName
    ]
  );

  return (
    <EditDashboardDraftContext.Provider value={value}>
      {children}
    </EditDashboardDraftContext.Provider>
  );
};

export const useEditDashboardDraft = () => {
  const ctx = useContext(EditDashboardDraftContext);
  if (!ctx) throw new Error('useEditDashboardDraft must be used within EditDashboardDraftProvider');
  return ctx;
};
